import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Box, Button, Card, CircularProgress, FormLabel, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AlignIcon,
  DeleteIcon,
  SuccessIcon,
  WarningFilledIcon,
} from '@sharedCR/coreDesignSystem/icons';
import ButtonWithTooltip from 'app/shared/components/common/ButtonWithTooltip';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { PromiseStatus } from 'constants/promise';
import { compressFile } from 'utils/file';
import { logger } from 'utils/logger';

import { showMessageAction } from 'store/global/actions';

import { FormTestIds } from '../form.testIds';

import { HTTP_STATUS_OK } from './FileUploadZone.constants';
import { getStyles } from './FileUploadZone.styles';

const defaultArray = [];

const getFileExtensionFromName = fileName => _.last(fileName.split('.'));

const FileUploadZone = ({
  accept = '',
  disabled = false,
  isMultiple = false,
  uploadFile = _.noop,
  className = '',
  isFilesPreviewShown = true,
  fileUploadLabel = 'Choose a file or drop it here',
  noValidate = false,
  id = 'document-upload',
  isButtonsShown = true,
  getFiles = _.noop,
  fullWidth = false,
  defaultFileList = defaultArray,
  disallowedTypes = defaultArray,
}) => {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const [files, setFiles] = useState(defaultFileList || []);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [isDragEnter, setIsDragEnter] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const theme = useTheme();
  const classes = useMemo(() => getStyles(theme, { isDragEnter }), [theme, isDragEnter]);
  const formWrapperSx = useMemo(
    () =>
      !className ? { ...classes.formWrapper, ...(fullWidth ? classes.fullWidth : {}) } : undefined,
    [className, classes.formWrapper, classes.fullWidth, fullWidth],
  );

  useEffect(() => {
    getFiles(files);
  }, [files, getFiles]);

  const clearFileInput = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const validateFiles = useCallback(
    files => {
      const validatedFiles = [...files].filter(file => {
        if (disallowedTypes.length) {
          const isDisallowed = disallowedTypes.includes(getFileExtensionFromName(file.name));

          return !isDisallowed;
        }

        const acceptedTypesArr = accept.split(',');

        return acceptedTypesArr.includes(file.type);
      });

      const hasDisallowedFiles =
        validatedFiles.length < [...files].length && disallowedTypes.length;

      if (hasDisallowedFiles) {
        dispatch(
          showMessageAction({
            message:
              'File(s) type is blocked because its content presents a potential security issue',
            variant: 'error',
          }),
        );
      }

      return validatedFiles;
    },
    [dispatch, accept, disallowedTypes],
  );

  const setUniqueFiles = useCallback(
    files =>
      setFiles(prevFiles => {
        const allFiles = prevFiles.concat(...files);

        const uniqueFilesArray = _.uniqBy(allFiles, 'name').slice(isMultiple ? 0 : -1);

        if (isMultiple && uniqueFilesArray.length < allFiles.length) {
          dispatch(
            showMessageAction({
              message: 'File with the same name is already uploaded',
              variant: 'error',
            }),
          );
        }

        clearFileInput();

        return uniqueFilesArray;
      }),
    [clearFileInput, dispatch, isMultiple],
  );

  const onFileInputChange = useCallback(
    e => {
      const { files } = e.target;

      if (files) {
        const filesToSet = noValidate ? files : validateFiles(files);
        setUniqueFiles(filesToSet);
      }
    },
    [noValidate, setUniqueFiles, validateFiles],
  );

  const onRemoveFile = e => {
    e.preventDefault();
    const { name } = e.target;

    if (name) {
      setFiles(prevFiles => prevFiles.filter(file => file.name !== name));
      clearFileInput();
    }
  };

  const onBrowseButtonClick = useCallback(e => {
    e.preventDefault();

    fileInputRef?.current?.click();
  }, []);

  const onClearClick = useCallback(
    e => {
      e.preventDefault();

      setFiles([]);
      clearFileInput();
    },
    [clearFileInput],
  );

  const onDragEnter = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragEnter(true);
  }, []);

  const onDragOver = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const onDragLeaveHandler = useCallback(e => {
    e.preventDefault();
    e.stopPropagation();

    if (e.target === e.currentTarget) {
      setIsDragEnter(false);
    }
  }, []);

  const onDropFile = useCallback(
    e => {
      e.preventDefault();
      e.stopPropagation();

      setIsDragEnter(false);

      const dt = e.dataTransfer;

      if (dt.files) {
        const files = noValidate ? dt.files : validateFiles(dt.files);
        setUniqueFiles(files);
      }
    },
    [setUniqueFiles, noValidate, validateFiles],
  );

  const onUploadFiles = useCallback(
    async e => {
      e.preventDefault();

      if (!files.length) {
        return;
      }

      const uploadQueue = files.map(file =>
        compressFile(file)
          .then(uploadFile)
          .catch(error => {
            dispatch(showMessageAction({ message: error?.message, variant: 'error' }));
            logger.error(error);
          }),
      );

      try {
        setIsUploading(true);

        const result = await Promise.allSettled(uploadQueue);
        const uploadedFiles = result.map(({ value, reason }, index) => ({
          status:
            value?.status === HTTP_STATUS_OK ? PromiseStatus.FULFILLED : PromiseStatus.REJECTED,
          value,
          reason,
          name: files[index].name,
        }));

        setUploadedFiles(prevUploadedFiles =>
          _.uniqBy(prevUploadedFiles.concat(...uploadedFiles), 'name'),
        );
        setFiles([]);
        clearFileInput();
      } catch {
        // do nothing, coz errors already have been processed by Promise.allSettled
      } finally {
        setIsUploading(false);
      }
    },
    [clearFileInput, files, uploadFile, dispatch],
  );

  const onPreventClick = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const renderError = reason => {
    if (!reason) {
      return null;
    }

    const errorText = reason?.response?.data || reason.message || 'Unexpected error :(';

    return (
      <Typography
        className="pt-2"
        data-testid={FormTestIds.FILE_UPLOAD_ZONE_ERROR_TEXT_LABEL}
        sx={classes.errorText}
        variant="medium12"
      >
        {errorText}
      </Typography>
    );
  };

  const renderIcon = (fileName, status = PromiseStatus.PENDING) => {
    switch (status) {
      case PromiseStatus.PENDING:
        if (isUploading) {
          return (
            <CircularProgress
              color="inherit"
              data-testid={FormTestIds.FILE_UPLOAD_ZONE_SPINNER}
              size={24}
            />
          );
        }

        return (
          <ButtonWithTooltip
            data-testid={FormTestIds.FILE_UPLOAD_ZONE_DELETE_BUTTON}
            icon={DeleteIcon}
            iconButtonClassName="text-white [&>*]:pointer-events-none"
            iconClassName="tool__disable-interaction"
            iconSize={IconSize[24]}
            name={fileName}
            size="small"
            title="Delete file"
            variant="outlined"
            onClick={onRemoveFile}
          />
        );

      case PromiseStatus.FULFILLED:
        return (
          <Box
            component={SuccessIcon}
            data-testid={FormTestIds.FILE_UPLOAD_ZONE_SUCCESS_ICON}
            size={IconSize[24]}
            sx={classes.successIcon}
          />
        );

      case PromiseStatus.REJECTED:
        return (
          <WarningFilledIcon
            data-testid={FormTestIds.FILE_UPLOAD_ZONE_ERROR_ICON}
            size={IconSize[24]}
            style={classes.errorText}
          />
        );

      default:
        return null;
    }
  };

  const renderFileCard = (fileName, status, reason) => {
    return (
      <div className="flex justify-between items-center w-full p-12">
        <div>
          <Typography
            className="tool__overflow-ellipsis text-white"
            sx={classes.fileName}
            variant="medium14"
          >
            {fileName}
          </Typography>
          {renderError(reason)}
        </div>
        {renderIcon(fileName, status)}
      </div>
    );
  };

  return (
    <>
      <Box className={className || 'flex flex-col justify-center p-24'} sx={formWrapperSx}>
        {isFilesPreviewShown && (
          <Box sx={classes.cardsWrapper}>
            {uploadedFiles.map(({ name, status, reason }) => (
              <Card
                className="w-full mb-16 rounded-4"
                data-testid={FormTestIds.FILE_UPLOAD_ZONE_UPLOADED_FILE_CARD}
                elevation={2}
                key={name}
                sx={classes.fileCard}
              >
                {renderFileCard(name, status, reason)}
              </Card>
            ))}
          </Box>
        )}
        <input
          accept={accept}
          className="tool__visually-hidden"
          data-testid={FormTestIds.FILE_UPLOAD_ZONE_FILE_INPUT}
          disabled={disabled}
          id={id}
          multiple={!!isMultiple}
          ref={fileInputRef}
          style={classes.fileInput}
          type="file"
          onChange={onFileInputChange}
        />
        <FormLabel
          data-testid={FormTestIds.FILE_UPLOAD_ZONE_DROPZONE}
          disabled={disabled}
          htmlFor={id}
          sx={classes.label}
          onDragEnter={onDragEnter}
          onDragLeave={onDragLeaveHandler}
          onDragOver={onDragOver}
          onDrop={onDropFile}
        >
          {files.map(({ name }) => (
            <Card
              className="w-full mb-16 rounded-4"
              data-testid={FormTestIds.FILE_UPLOAD_ZONE_SELECTED_FILE_CARD}
              elevation={2}
              key={name}
              sx={classes.fileCard}
              onClick={onPreventClick}
            >
              {renderFileCard(name)}
            </Card>
          ))}
          <Box
            className="flex flex-col justify-center items-center tool__disable-interaction"
            sx={classes.dndTip}
          >
            <AlignIcon direction={IconDirection.UP} size={IconSize[40]} />
            <Typography className="text-center" variant="medium16">
              {fileUploadLabel}
            </Typography>
          </Box>
        </FormLabel>
        {isButtonsShown && (
          <Box className="flex w-full items-center" sx={classes.buttonsContainer}>
            <Button
              data-testid={FormTestIds.FILE_UPLOAD_ZONE_BROWSE_BUTTON}
              disabled={isUploading}
              variant="line"
              onClick={onBrowseButtonClick}
            >
              Browse
            </Button>
            <Button
              data-testid={FormTestIds.FILE_UPLOAD_ZONE_CLEAR_BUTTON}
              disabled={!files.length || isUploading}
              variant="line"
              onClick={onClearClick}
            >
              Clear
            </Button>
            <Button
              color="primary"
              data-testid={FormTestIds.FILE_UPLOAD_ZONE_UPLOAD_BUTTON}
              disabled={!files.length || isUploading || disabled}
              type="button"
              variant="contained"
              onClick={onUploadFiles}
            >
              Upload
            </Button>
          </Box>
        )}
      </Box>
    </>
  );
};

FileUploadZone.propTypes = {
  uploadFile: PropTypes.func,
  isMultiple: PropTypes.bool,
  accept: PropTypes.string,
  disabled: PropTypes.bool,
  noValidate: PropTypes.bool,
  isButtonsShown: PropTypes.bool,
  isFilesPreviewShown: PropTypes.bool,
  fileUploadLabel: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
  getFiles: PropTypes.func,
  fullWidth: PropTypes.bool,
  defaultFileList: PropTypes.arrayOf(PropTypes.object),
  disallowedTypes: PropTypes.arrayOf(PropTypes.string),
};

export default memo(FileUploadZone);
